import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Download, ExternalLink } from 'lucide-react';

const DataTable = ({ codes, dataMap, columns, onFavorite, favorites = [] }) => {
  if (!codes || codes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <div className="text-4xl mb-4">📋</div>
        <p className="text-lg font-medium">No codes found</p>
        <p className="text-sm mt-2">Try adjusting your search criteria</p>
      </div>
    );
  }

  const isFavorite = (code) => favorites.includes(code);

  return (
    <div className="overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Table Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Medical Codes ({codes.length} results)
          </h3>
          <button className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {codes.map((c, i) => {
              const item = dataMap[c.code] || c;
              return (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Actions Column */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <motion.button
                        onClick={() => onFavorite && onFavorite(item.code)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isFavorite(item.code)
                            ? 'text-red-500 hover:text-red-600 bg-red-50 dark:bg-red-900/20'
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={isFavorite(item.code) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Heart className={`h-4 w-4 ${isFavorite(item.code) ? 'fill-current' : ''}`} />
                      </motion.button>
                      <button
                        className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="View details"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </td>

                  {/* Data Columns */}
                  {columns.map((col) => {
                    let val = item[col.key];
                    // Fallback for CPT uppercase vs lowercase
                    if (val === undefined) {
                      const alt = col.key
                        .split("_")
                        .map((w) => w[0].toUpperCase() + w.slice(1))
                        .join("");
                      val = item[alt] ?? "";
                    }

                    // Format currency values
                    if (col.key.includes('payment') || col.key.includes('copay') || col.key.includes('Payment') || col.key.includes('Copay')) {
                      val = val ? `$${parseFloat(val).toFixed(2)}` : '';
                    }

                    // Format RVU values
                    if (col.key.includes('rvu') || col.key.includes('RVU')) {
                      val = val ? parseFloat(val).toFixed(2) : '';
                    }

                    return (
                      <td
                        key={col.key}
                        className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100"
                      >
                        {col.key === 'code' ? (
                          <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                            {val}
                          </span>
                        ) : col.key === 'description' ? (
                          <div className="">
                            <p className="font-medium text-gray-900 dark:text-white whitespace-normal break-words" title={val}>
                              {val}
                            </p>
                          </div>
                        ) : (
                          <span className={`${
                            col.key.includes('payment') || col.key.includes('copay') || col.key.includes('Payment') || col.key.includes('Copay')
                              ? 'font-semibold text-green-600 dark:text-green-400'
                              : col.key.includes('rvu') || col.key.includes('RVU')
                              ? 'font-medium text-purple-600 dark:text-purple-400'
                              : ''
                          }`}>
                            {val || '-'}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>Showing {codes.length} results</span>
          <span>Professional Medical Coding Data</span>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
